import axios from 'axios';
import appConfig from '../config';
import authService from './authService';
import config from "../config";

const niraApi = axios.create({
    baseURL: `${appConfig.NIRA_API_BASE_ENDPOINT}`,
    timeout: config.TIMEOUTS.API_REQUEST,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
    withCredentials: true,
});

niraApi.interceptors.request.use(
    (cfg) => {
        const token = localStorage.getItem('accessToken');
        if (token) cfg.headers.Authorization = `Bearer ${token}`;
        return cfg;
    },
    (error) => Promise.reject(error)
);

const niraService = {

    postIntegrationConfig: async (integration, payload) => {
        if (!integration) throw new Error("integration is required");

        let endpointPath;
        switch (integration) {
            case "nira":
                endpointPath = appConfig.NIRA_ADMIN_ENDPOINTS.CREATE_CONFIG;
                break;
            default:
                throw new Error(
                    `No admin endpoint configured for integration '${integration}'`
                );
        }

        const { data } = await niraApi.post(endpointPath, payload);
        return data;
    },

    getPaginated: async (endpointPath, {
        page = 0,
        size = appConfig.PAGINATION.DEFAULT_LIMIT || 10,
        sort,
        query = {}
    } = {}) => {
        if (!endpointPath) throw new Error('endpointPath is required');

        const params = {page, size, ...query};
        if (sort) params.sort = sort;

        const {data} = await niraApi.get(endpointPath, {params});

        const payload = data?.data ?? data;
        return normalizePage(payload);
    },

    fetchNiraVerificationRequestsPage: async ({
                                                  page = 0,
                                                  size = appConfig.PAGINATION.DEFAULT_LIMIT || 10,
                                                  sort,
                                                  query = {}
                                              } = {}) => {
        const endpoint = appConfig.NIRA_ADMIN_ENDPOINTS.FETCH_VERIFICATION_REQUESTS;
        return niraService.getPaginated(endpoint, {page, size, sort, query});
    },

    verifyPerson: async (payload) => {
        if (!payload?.nationalId) throw new Error('nationalId is required');
        if (!payload?.documentId) throw new Error('documentId is required');
        const endpointPath = appConfig.NIRA_VERIFY_IDENTITY.VERIFY_PERSONS;
        const { data } = await niraApi.post(endpointPath, payload);
        return data;
    },

    runAllIntegrationTests: async (payload) => {
        const endpointPath = appConfig.NIRA_VERIFY_IDENTITY.RUN_ALL_TESTS;
        const { data } = await niraApi.post(endpointPath, payload);
        return data;
    },

    resetNiraPassword: async (payload) => {
        if (!payload?.newPassword) throw new Error('newPassword is required');
        const endpointPath = appConfig.NIRA_ADMIN_ENDPOINTS.RESET_NIRA_PASSWORD;
        const { data } = await niraApi.post(endpointPath, payload);
        return data;
    },

    fetchNiraVerificationRequest: async (id) => {
        if(!id) throw new Error('id is required to fetch verification request');
        const endpointPath = appConfig.NIRA_ADMIN_ENDPOINTS.FETCH_VERIFICATION_REQUEST;
        const { data } = await niraApi.get(endpointPath, {params: {id}});
        return data;
    },
    fetchNiraConfiguration: async () => {
        const endpointPath = appConfig.NIRA_ADMIN_ENDPOINTS.FETCH_CONFIG;
        const { data } = await niraApi.get(endpointPath);
        return data;
    },
    fetchAuditTrails: async (filterData) => {
        try {
            const accessToken = await authService.getValidAccessToken();
            const payload = {
                ...filterData,
                accessToken
            };
            const endpointPath = appConfig.NIRA_ADMIN_ENDPOINTS.GET_AUDIT_TRAILS;
            const response = await niraApi.post(endpointPath, payload);
            return response.data;
        } catch (error) {
            console.error('Error getting Nira logs:', error.message);
            throw error.message;
        }
    },

    fetchCountsByStatus: async (params) => {
        try {
            const accessToken = await authService.getValidAccessToken();
            const queryParams = new URLSearchParams({
                rangeType: params.rangeType || 'LAST_1_MONTH'
            });

            if (params.rangeType === 'CUSTOM_RANGE') {
                if (params.customStart) queryParams.append('customStart', params.customStart);
                if (params.customEnd) queryParams.append('customEnd', params.customEnd);
            }

            const endpointPath = `${appConfig.NIRA_ADMIN_ENDPOINTS.GET_COUNTS_BY_STATUS}?${queryParams.toString()}`;
            const response = await niraApi.get(endpointPath, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching counts by status:', error.message);
            throw error;
        }
    },

    fetchCurrentDateVerification: async () => {
        try {
            const accessToken = await authService.getValidAccessToken();
            const endpointPath = `${appConfig.NIRA_ADMIN_ENDPOINTS.GET_VERIFICATIONS_TODAY}`;
            const response = await niraApi.get(endpointPath, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching today verifications: ', error.message);
            throw error;
        }
    },

    fetchTrendsByMonth: async (params) => {
        try {
            const accessToken = await authService.getValidAccessToken();
            const queryParams = new URLSearchParams({
                rangeType: params.rangeType || 'LAST_1_MONTH'
            });

            if (params.rangeType === 'CUSTOM_RANGE') {
                if (params.customStart) queryParams.append('customStart', params.customStart);
                if (params.customEnd) queryParams.append('customEnd', params.customEnd);
            }

            const endpointPath = `${appConfig.NIRA_ADMIN_ENDPOINTS.GET_TRENDS_BY_MONTH}?${queryParams.toString()}`;
            const response = await niraApi.get(endpointPath, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching trends by month:', error.message);
            throw error;
        }
    },

    fetchNiraStatistics: async (params) => {
        try {
            const [statusCountsResponse, trendsResponse] = await Promise.all([
                niraService.fetchCountsByStatus(params),
                niraService.fetchTrendsByMonth(params)
            ]);

            return {
                success: true,
                statusCounts: statusCountsResponse.data || [],
                trendsByMonth: trendsResponse.data || []
            };
        } catch (error) {
            console.error('Error fetching NIRA statistics:', error.message);
            throw error;
        }
    },
};

function normalizePage(respData) {
    if (!respData) return {
        content: [], totalElements: 0, totalPages: 0, number: 0, size: 0,
        first: true, last: true, empty: true, sort: undefined,
    };

    const {
        content = [],
        totalElements = 0,
        totalPages = 0,
        number = 0,
        size = content?.length || 0,
        first = number === 0,
        last = totalPages ? number >= totalPages - 1 : true,
        empty = content.length === 0,
        sort,
    } = respData;

    return {content, totalElements, totalPages, number, size, first, last, empty, sort};
}

export default niraService;